import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Highlighter from 'react-highlight-words';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';
import { Dialog, TextField, Toggle, Button } from '../../components/widgets';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { getUserData } from '../../selectors/user';
import { SHOW_IMPERSONATION_MODAL_EVENT } from '../consts';
import { getAllUsers, impersonateUser } from '../impersonateUserService';
import {
    computeVisibleUsers,
    deriveTiers,
    buildImpersonateUrl,
    buildUserDetails,
    canImpersonate,
    impersonationBlockedReason,
    isCurrentUser,
    formatUserSubtitle,
    getTierKey,
    hasChipFilters,
    DEFAULT_FILTERS,
    LABELLED_TIERS,
    RESULTS_LIMIT,
    ROLE_ADMIN,
    ROLE_STANDARD,
    ROLE_RESTRICTED,
    TIER_STANDARD_ANALYTICS,
} from '../impersonateUserFilters';
import defaultUserIcon from '../../resources/png/default_user_icon.png';
import css from './ImpersonateUserDialog.css';

const KEY = 'STATIC.TOPBAR_HEADERS.IMPERSONATE_MODAL';

const ROLE_CHIPS = [ROLE_ADMIN, ROLE_STANDARD, ROLE_RESTRICTED];

const ANALYTICS_CHIP = { name: 'showAnalyticsOnly', labelKey: 'ANALYTICS_ONLY' };

const INTERACTIVE_TARGET = 'button, a, [role="switch"], input[type="checkbox"]';

const LISTBOX_ID = 'impersonate-user-listbox';

const LOAD_MORE_THRESHOLD_PX = 120;

const SKELETON_ROW_COUNT = 8;

const SKELETON_ROW_WIDTHS = [
    ['38%', '64%'],
    ['27%', '49%'],
    ['46%', '71%'],
    ['32%', '55%'],
    ['41%', '61%'],
    ['29%', '46%'],
    ['44%', '68%'],
    ['35%', '52%'],
];

const SKELETON_ROLE_CHIP_WIDTHS = [66, 54, 70, 76];
const SKELETON_TIER_CHIP_WIDTHS = [82, 58, 74];

const SKELETON_FIELD_WIDTHS = [
    [72, 128],
    [64, 44],
    [56, 116],
    [30, 68],
    [46, 50],
    [70, 60],
    [76, 34],
    [28, 52],
    [66, 42],
];

const rowDomId = userId => `imp-user-${userId}`;

const optionKey = option => option.user_obj.user_id;
const isEmptyValue = value => value === null || value === undefined || value === '';

function TierLabel({ tier }) {
    if (!LABELLED_TIERS.includes(tier)) {
        return tier || null;
    }
    return <Translate id={`${KEY}.TIERS.${tier}`} />;
}

TierLabel.propTypes = { tier: PropTypes.string };

function RoleLabel({ role }) {
    if (!ROLE_CHIPS.includes(role)) {
        return role || null;
    }
    return <Translate id={`${KEY}.ROLES.${role.toUpperCase()}`} />;
}

RoleLabel.propTypes = { role: PropTypes.string };

function DetailValue({ value, valueKey, valueData, tier, href }) {
    if (href) {
        return (
            <a className={css.detailFieldLink} href={href} target="_blank" rel="noopener noreferrer">
                {String(value)}
            </a>
        );
    }
    if (tier) {
        return <TierLabel tier={tier} />;
    }
    if (isEmptyValue(value)) {
        return <Translate id={`${KEY}.${valueKey}`} data={valueData} />;
    }
    return String(value);
}

DetailValue.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    valueKey: PropTypes.string,
    valueData: PropTypes.shape({ count: PropTypes.number }),
    tier: PropTypes.string,
    href: PropTypes.string,
};

function Match({ search, text, className, element = 'span' }) {
    return React.createElement(
        element,
        { className },
        <Highlighter
            autoEscape
            searchWords={[search]}
            textToHighlight={text || ''}
            highlightClassName={css.highlighted}
        />
    );
}

Match.propTypes = {
    search: PropTypes.string.isRequired,
    text: PropTypes.string,
    className: PropTypes.string,
    element: PropTypes.oneOf(['span', 'div']),
};

function SkeletonChipRow() {
    return (
        <div className={css.chipRow} aria-hidden="true">
            {SKELETON_ROLE_CHIP_WIDTHS.map(width => (
                <Skeleton key={`role-${width}`} variant="rectangular" className={css.skeletonChip} width={width} />
            ))}
            <div className={css.chipDivider} />
            {SKELETON_TIER_CHIP_WIDTHS.map(width => (
                <Skeleton key={`tier-${width}`} variant="rectangular" className={css.skeletonChip} width={width} />
            ))}
            <div className={css.chipSpacer} />
            <Skeleton variant="rectangular" className={css.skeletonToggleTrack} />
            <Skeleton variant="rectangular" className={css.skeletonToggleLabel} />
        </div>
    );
}

function SkeletonList() {
    return (
        <>
            <div className={css.listHeader} aria-hidden="true">
                <Skeleton variant="rectangular" className={css.skeletonHeaderLabel} />
                <Skeleton variant="rectangular" className={css.skeletonHeaderCount} />
            </div>
            <div className={css.skeletonRows} role="status" aria-label="loading users">
                {SKELETON_ROW_WIDTHS.slice(0, SKELETON_ROW_COUNT).map(([titleWidth, metaWidth]) => (
                    <div key={titleWidth} className={css.skeletonRow} aria-hidden="true">
                        <Skeleton variant="rectangular" className={css.skeletonAvatar} />
                        <div className={css.skeletonRowText}>
                            <Skeleton variant="rectangular" className={css.skeletonRowTitle} width={titleWidth} />
                            <Skeleton variant="rectangular" className={css.skeletonRowMeta} width={metaWidth} />
                        </div>
                        <Skeleton variant="rectangular" className={css.skeletonTierBadge} />
                    </div>
                ))}
            </div>
        </>
    );
}

function SkeletonDetail() {
    return (
        <div className={css.skeletonDetail} role="status" aria-label="loading account details">
            <div className={css.detailScroll} aria-hidden="true">
                <div className={css.detailIdentity}>
                    <Skeleton variant="rectangular" className={css.skeletonDetailAvatar} />
                    <div className={css.detailIdentityText}>
                        <Skeleton variant="rectangular" className={css.skeletonDetailName} />
                        <Skeleton variant="rectangular" className={css.skeletonDetailEmail} />
                    </div>
                </div>
                <div className={css.detailFields}>
                    {SKELETON_FIELD_WIDTHS.map(([labelWidth, valueWidth]) => (
                        <div key={labelWidth} className={css.detailField}>
                            <Skeleton
                                variant="rectangular"
                                className={css.skeletonDetailFieldLabel}
                                width={labelWidth}
                            />
                            <Skeleton
                                variant="rectangular"
                                className={css.skeletonDetailFieldValue}
                                width={valueWidth}
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div className={css.detailFooter} aria-hidden="true">
                <Skeleton variant="rectangular" className={css.skeletonFooterHint} />
                <Skeleton variant="rectangular" className={css.skeletonImpersonateButton} />
            </div>
        </div>
    );
}

function ImpersonateUserDialog() {
    const userData = useSelector(getUserData) || {};
    const isImpersonator = !!userData.is_impersonator;
    const currentUserId = userData.user_id;

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [selectedId, setSelectedId] = useState(null);
    const [limit, setLimit] = useState(RESULTS_LIMIT);
    const [pending, setPending] = useState(false);
    const [startFailedFor, setStartFailedFor] = useState(null);
    const fetchedRef = useRef(false);
    const pendingRef = useRef(false);
    const inFlightRef = useRef(false);
    const headerRef = useRef(null);
    const growLockRef = useRef(false);
    const pendingIndexRef = useRef(null);

    const loadUsers = useCallback(() => {
        if (fetchedRef.current) {
            return;
        }
        fetchedRef.current = true;
        setLoading(true);
        setLoadFailed(false);
        getAllUsers()
            .then(users => {
                setAllUsers(users);
            })
            .catch(() => {
                fetchedRef.current = false;
                setLoadFailed(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const retryLoad = useCallback(() => {
        fetchedRef.current = false;
        setLoadFailed(false);
        loadUsers();
    }, [loadUsers]);

    const openDialog = useCallback(() => {
        setOpen(true);
        loadUsers();
    }, [loadUsers]);

    useEffect(() => {
        if (!isImpersonator) {
            return undefined;
        }
        const onEvent = () => openDialog();
        const onKeyDown = event => {
            if (event.ctrlKey && (event.key === 'i' || event.key === 'I')) {
                const { target } = event;
                const tagName = target && target.tagName;
                if (
                    tagName === 'INPUT' ||
                    tagName === 'SELECT' ||
                    tagName === 'TEXTAREA' ||
                    target?.isContentEditable
                ) {
                    return;
                }
                event.preventDefault();
                openDialog();
            }
        };
        window.addEventListener(SHOW_IMPERSONATION_MODAL_EVENT, onEvent);
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener(SHOW_IMPERSONATION_MODAL_EVENT, onEvent);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isImpersonator, openDialog]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onNavigate = () => setOpen(false);
        window.addEventListener('hashchange', onNavigate);
        window.addEventListener('popstate', onNavigate);
        return () => {
            window.removeEventListener('hashchange', onNavigate);
            window.removeEventListener('popstate', onNavigate);
        };
    }, [open]);

    const tiers = useMemo(() => deriveTiers(allUsers), [allUsers]);
    const { options, total, uniqueTotal, searching } = useMemo(
        () => computeVisibleUsers({ allUsers, searchString: search, filters, limit }),
        [allUsers, search, filters, limit]
    );
    const hasMore = options.length < total;

    const loadMore = useCallback(() => {
        if (growLockRef.current) {
            return;
        }
        growLockRef.current = true;
        setLimit(prev => prev + RESULTS_LIMIT);
    }, []);

    useEffect(() => {
        growLockRef.current = false;
    }, [options]);

    useEffect(() => {
        const index = pendingIndexRef.current;
        if (index !== null && index < options.length) {
            pendingIndexRef.current = null;
            setSelectedId(optionKey(options[index]));
        }
    }, [options]);

    const onListScroll = useCallback(
        event => {
            if (!hasMore) {
                return;
            }
            const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
            if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX) {
                loadMore();
            }
        },
        [hasMore, loadMore]
    );

    const selected = useMemo(() => options.find(option => optionKey(option) === selectedId) || null, [
        options,
        selectedId,
    ]);
    const selectedKey = selected ? optionKey(selected) : null;

    useEffect(() => {
        setSelectedId(null);
        setStartFailedFor(null);
        pendingIndexRef.current = null;
        setLimit(RESULTS_LIMIT);
    }, [open, search, filters]);

    useEffect(() => {
        if (!open) {
            return;
        }

        if (!inFlightRef.current) {
            pendingRef.current = false;
            setPending(false);
        }

        setSearch('');
        setFilters(DEFAULT_FILTERS);
    }, [open]);

    useEffect(() => {
        if (selectedKey !== null && selectedKey !== undefined) {
            document.getElementById(rowDomId(selectedKey))?.scrollIntoView({ block: 'nearest' });
        }
    }, [selectedKey]);

    const close = useCallback(() => setOpen(false), []);

    const startSession = useCallback(
        option => {
            if (!option || pendingRef.current || !canImpersonate(option, currentUserId)) {
                return;
            }
            const { user_obj: user } = option;
            pendingRef.current = true;
            inFlightRef.current = true;
            setPending(true);
            setStartFailedFor(null);
            impersonateUser(user.user_id)
                .then(() => {
                    inFlightRef.current = false;
                    localStorage.clear();
                    window.location.href = buildImpersonateUrl(user.username, window.location.hash);
                })
                .catch(() => {
                    inFlightRef.current = false;
                    pendingRef.current = false;
                    setPending(false);
                    setStartFailedFor(user.username);
                });
        },
        [currentUserId]
    );

    const moveSelection = useCallback(
        step => {
            if (!options.length) {
                return;
            }
            const current = selected ? options.findIndex(option => optionKey(option) === optionKey(selected)) : -1;
            const next = current + step;
            if (next >= options.length) {
                if (hasMore) {
                    pendingIndexRef.current = options.length;
                    loadMore();
                }
                return;
            }
            const nextOption = options[Math.max(0, next)];
            if (nextOption) {
                setSelectedId(optionKey(nextOption));
            }
        },
        [options, selected, hasMore, loadMore]
    );

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKeyDown = event => {
            const surface = headerRef.current?.parentNode;
            const { target } = event;
            if (!surface || !target || !target.nodeType || !surface.contains(target)) {
                return;
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                moveSelection(1);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                moveSelection(-1);
                return;
            }
            if (event.key !== 'Enter' || (target.closest && target.closest(INTERACTIVE_TARGET))) {
                return;
            }
            event.preventDefault();
            startSession(selected);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, moveSelection, selected, startSession]);

    if (!isImpersonator) {
        return null;
    }

    const setFilter = (name, value) => setFilters(prev => ({ ...prev, [name]: value }));
    const toggleFilter = name => setFilter(name, !filters[name]);
    const clearFilters = () => setFilters(prev => ({ ...DEFAULT_FILTERS, showDevAccounts: prev.showDevAccounts }));
    const filtersApplied = hasChipFilters(filters);
    const usersLoaded = allUsers.length > 0;
    const showSkeletons = !usersLoaded && loading;

    const detailRows = buildUserDetails(selected);
    const selectedUser = selected ? selected.user_obj : null;
    const blockedReason = impersonationBlockedReason(selected, currentUserId);
    const canStart = Boolean(selected) && !blockedReason && !pending;

    const renderBooleanChip = ({ name, labelKey }) => (
        <button
            key={name}
            type="button"
            aria-pressed={filters[name]}
            className={classNames(css.chip, { [css.chipActive]: filters[name] })}
            onClick={() => toggleFilter(name)}
        >
            <Translate id={`${KEY}.${labelKey}`} />
        </button>
    );

    const renderRoleChips = () =>
        ROLE_CHIPS.map(role => (
            <button
                key={role}
                type="button"
                aria-pressed={filters.rolefilter === role}
                className={classNames(css.chip, { [css.chipActive]: filters.rolefilter === role })}
                onClick={() => setFilter('rolefilter', filters.rolefilter === role ? '' : role)}
            >
                <RoleLabel role={role} />
            </button>
        ));

    const renderTierChips = () => {
        if (tiers.length <= 1) {
            return [renderBooleanChip(ANALYTICS_CHIP)];
        }
        const chips = [];
        tiers.forEach(tier => {
            if (tier === TIER_STANDARD_ANALYTICS) {
                chips.push(renderBooleanChip(ANALYTICS_CHIP));
                return;
            }
            chips.push(
                <button
                    key={tier}
                    type="button"
                    aria-pressed={filters.tierfilter === tier}
                    className={classNames(css.chip, { [css.chipActive]: filters.tierfilter === tier })}
                    onClick={() => setFilter('tierfilter', filters.tierfilter === tier ? '' : tier)}
                >
                    <TierLabel tier={tier} />
                </button>
            );
        });
        if (!tiers.includes(TIER_STANDARD_ANALYTICS)) {
            chips.push(renderBooleanChip(ANALYTICS_CHIP));
        }
        return chips;
    };
    const tierChips = renderTierChips();

    const totalLabel = total.toLocaleString();
    const renderCount = () => {
        if (hasMore) {
            return (
                <Translate
                    id={`${KEY}.COUNT_SHOWING_FIRST`}
                    data={{ limit: options.length.toLocaleString(), total: totalLabel }}
                />
            );
        }
        if (searching) {
            return <Translate id={`${KEY}.COUNT_FOUND`} data={{ count: totalLabel }} />;
        }
        return (
            <Translate id={`${KEY}.COUNT_OF_TOTAL`} data={{ count: totalLabel, total: uniqueTotal.toLocaleString() }} />
        );
    };

    let headerLabelKey = 'ALL_USERS';
    if (searching) {
        headerLabelKey = 'MATCHES';
    } else if (filtersApplied) {
        headerLabelKey = 'FILTERED';
    }

    const renderEmptyState = () => (
        <div className={css.empty}>
            <div>
                {searching ? (
                    <Translate id={`${KEY}.EMPTY_TITLE`} data={{ query: search }} />
                ) : (
                    <Translate id={`${KEY}.EMPTY_FILTER_TITLE`} />
                )}
            </div>
            <div className={css.emptyHint}>
                <Translate id={searching ? `${KEY}.EMPTY_HINT` : `${KEY}.EMPTY_FILTER_HINT`} />
            </div>
        </div>
    );

    return (
        <Dialog open={open} noPadding className={css.dialog} onEscapePress={close} onClose={close}>
            <>
                <header className={css.header} ref={headerRef}>
                    <h3 className={css.title}>
                        <Translate id={`${KEY}.TITLE`} />
                    </h3>
                    <button type="button" className={css.closeButton} aria-label="close" onClick={close}>
                        <Icon name="x" size={IconSizes.SMALL} variant={IconVariants.LIGHT} />
                    </button>
                </header>

                <div className={css.controls}>
                    <TextField
                        placeholder={`${KEY}.SEARCH_PLACEHOLDER`}
                        value={search}
                        debounce={300}
                        clearable
                        autoCompleteOff
                        inputConfig={{
                            autoFocus: true,
                            'aria-label': 'search',
                            'aria-controls': LISTBOX_ID,
                            'aria-activedescendant':
                                selectedKey === null || selectedKey === undefined ? undefined : rowDomId(selectedKey),
                        }}
                        onChange={setSearch}
                    />
                    {usersLoaded ? (
                        <div className={css.chipRow}>
                            <button
                                type="button"
                                aria-pressed={!filtersApplied}
                                className={classNames(css.chip, { [css.chipActive]: !filtersApplied })}
                                onClick={clearFilters}
                            >
                                <Translate id={`${KEY}.ALL_USERS`} />
                            </button>
                            {renderRoleChips()}
                            <div className={css.chipDivider} />
                            {tierChips}
                            <div className={css.chipSpacer} />
                            <Toggle
                                label={<Translate id={`${KEY}.SHOW_INTERNAL_DEV`} />}
                                ariaLabel="show internal and dev accounts"
                                checked={filters.showDevAccounts}
                                onToggle={() => toggleFilter('showDevAccounts')}
                            />
                        </div>
                    ) : null}
                    {showSkeletons ? <SkeletonChipRow /> : null}
                </div>

                <div className={css.body}>
                    <div className={css.listPane} onScroll={onListScroll}>
                        {usersLoaded ? (
                            <div className={css.listHeader}>
                                <span className={css.listHeaderLabel}>
                                    <Translate id={`${KEY}.${headerLabelKey}`} />
                                </span>
                                <span className={css.listHeaderCount}>{renderCount()}</span>
                            </div>
                        ) : null}
                        {showSkeletons ? <SkeletonList /> : null}
                        {loadFailed ? (
                            <div className={css.errorState}>
                                <div className={css.errorMessage}>
                                    <Translate id={`${KEY}.LOAD_ERROR`} />
                                </div>
                                <button type="button" className={css.retryButton} onClick={retryLoad}>
                                    <Translate id={`${KEY}.RETRY`} />
                                </button>
                            </div>
                        ) : null}
                        {!loading && !loadFailed && !options.length ? renderEmptyState() : null}
                        <div
                            className={css.listOptions}
                            role="listbox"
                            id={LISTBOX_ID}
                            aria-label="users"
                            aria-busy={loading}
                        >
                            {options.map(option => {
                                const {
                                    user_obj: user,
                                    role,
                                    is_suspended: isSuspendedUser,
                                    org_count: orgCount,
                                } = option;
                                const tier = getTierKey(user);
                                const isSelected = selectedKey === optionKey(option);
                                return (
                                    <div
                                        key={user.user_id}
                                        id={rowDomId(user.user_id)}
                                        data-user-id={user.user_id}
                                        className={classNames(css.row, { [css.rowSelected]: isSelected })}
                                        role="option"
                                        tabIndex={-1}
                                        aria-selected={isSelected}
                                        onClick={() => setSelectedId(optionKey(option))}
                                        onDoubleClick={() => startSession(option)}
                                    >
                                        <img className={css.avatar} src={user.user_picture || defaultUserIcon} alt="" />
                                        <div className={css.rowText}>
                                            <div className={css.rowTitle}>
                                                <Match className={css.username} search={search} text={user.username} />
                                                <span
                                                    className={classNames(css.roleBadge, {
                                                        [css.roleAdmin]: role === ROLE_ADMIN,
                                                        [css.roleRestricted]: role === ROLE_RESTRICTED,
                                                    })}
                                                >
                                                    <RoleLabel role={role} />
                                                </span>
                                                {isCurrentUser(option, currentUserId) ? (
                                                    <span className={css.currentBadge}>
                                                        <Translate id={`${KEY}.CURRENT_SESSION`} />
                                                    </span>
                                                ) : null}
                                                {isSuspendedUser ? (
                                                    <span className={css.suspendedBadge}>
                                                        <Translate id={`${KEY}.SUSPENDED`} />
                                                    </span>
                                                ) : null}
                                                {orgCount > 1 ? (
                                                    <span className={css.multiOrgBadge}>
                                                        <Icon
                                                            name="alert-triangle"
                                                            size={IconSizes.XX_SMALL}
                                                            variant={IconVariants.ERROR}
                                                        />
                                                        {orgCount}
                                                    </span>
                                                ) : null}
                                                {user.is_old_permissions ? (
                                                    <span className={css.oldPerms}>
                                                        <Translate id={`${KEY}.OLD_PERMISSIONS`} />
                                                    </span>
                                                ) : null}
                                            </div>
                                            <Match
                                                element="div"
                                                className={css.rowMeta}
                                                search={search}
                                                text={formatUserSubtitle(user)}
                                            />
                                        </div>
                                        {tier ? (
                                            <span
                                                className={classNames(css.tierBadge, {
                                                    [css.tierExternal]: user.is_partner || user.is_agency,
                                                })}
                                            >
                                                <TierLabel tier={tier} />
                                            </span>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                        {hasMore ? (
                            <div className={css.listFooter}>
                                <button type="button" className={css.showMoreButton} onClick={loadMore}>
                                    <Translate
                                        id={`${KEY}.SHOW_MORE`}
                                        data={{ count: Math.min(RESULTS_LIMIT, total - options.length) }}
                                    />
                                </button>
                            </div>
                        ) : null}
                    </div>

                    <div className={css.detailPane}>
                        {selectedUser ? (
                            <>
                                <div className={css.detailScroll}>
                                    <div className={css.detailIdentity}>
                                        <img
                                            className={css.detailAvatar}
                                            src={selectedUser.user_picture || defaultUserIcon}
                                            alt=""
                                        />
                                        <div className={css.detailIdentityText}>
                                            <div className={css.detailName}>
                                                {`${selectedUser.first_name} ${selectedUser.last_name}`.trim() ||
                                                    selectedUser.username}
                                            </div>
                                            <div className={css.detailEmail}>{selectedUser.username}</div>
                                            {selected.is_suspended ? (
                                                <div className={css.suspendedNotice}>
                                                    <Translate id={`${KEY}.SUSPENDED_HINT`} />
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <dl className={css.detailFields}>
                                        {detailRows.map(row => (
                                            <div key={row.labelKey} className={css.detailField}>
                                                <dt className={css.detailFieldLabel}>
                                                    <Translate id={`${KEY}.FIELDS.${row.labelKey}`} />
                                                </dt>
                                                <dd className={css.detailFieldValue}>
                                                    <DetailValue
                                                        value={row.value}
                                                        valueKey={row.valueKey}
                                                        valueData={row.valueData}
                                                        tier={row.tier}
                                                        href={row.href}
                                                    />
                                                    {row.valueSuffix || null}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                                <div className={css.detailFooter}>
                                    {startFailedFor ? (
                                        <div className={css.errorBanner}>
                                            <Translate
                                                id={`${KEY}.IMPERSONATE_ERROR`}
                                                data={{ username: startFailedFor }}
                                            />
                                        </div>
                                    ) : null}
                                    {blockedReason ? (
                                        <div className={css.blockedNotice}>
                                            <Translate
                                                id={`${KEY}.${blockedReason.key}`}
                                                data={
                                                    blockedReason.count === undefined
                                                        ? undefined
                                                        : { count: blockedReason.count }
                                                }
                                            />
                                        </div>
                                    ) : null}
                                    <div className={css.footerHint}>
                                        <Translate id={`${KEY}.KEYBOARD_HINT`} />
                                    </div>
                                    <Button
                                        className={css.impersonateButton}
                                        disabled={!canStart}
                                        onClick={() => startSession(selected)}
                                        text={pending ? `${KEY}.IMPERSONATING` : `${KEY}.IMPERSONATE_SELECTED`}
                                    />
                                </div>
                            </>
                        ) : null}
                        {!selectedUser && showSkeletons ? <SkeletonDetail /> : null}
                        {!selectedUser && !showSkeletons ? (
                            <div className={css.noSelection}>
                                <Translate id={`${KEY}.NO_SELECTION`} />
                            </div>
                        ) : null}
                    </div>
                </div>
            </>
        </Dialog>
    );
}

export default ImpersonateUserDialog;
